import * as React from "react"
import { Head, router, usePage } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { InlineField } from "@/components/ui/inline-edit"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { useInlineRecord } from "@/hooks/use-inline-record"
import type { PageProps } from "@/types/inertia"
import { EmailLayout, type EmailNavProps } from "./EmailNav"

type Template = {
  key: string
  label: string
  description: string
  subject: string
  body: string
  enabled: boolean
  customised: boolean
  default_subject: string
  default_body: string
  variables: { name: string; description: string }[]
  preview: { subject: string; text: string }
}

type Props = PageProps & { template: Template; email_nav: EmailNavProps }

export default function TemplatePage() {
  const { template, email_nav, errors, flash } = usePage<Props>().props
  const url = `/admin/email/templates/${template.key}`
  const fields = useInlineRecord<{ subject: string; body: string }>({
    record: { subject: template.subject, body: template.body },
    url,
    param: "email_template",
  })
  const v = fields.values
  const subjectState = fields.state("subject")
  const bodyState = fields.state("body")
  const [testTo, setTestTo] = React.useState("")
  const [testing, setTesting] = React.useState(false)
  const bodyRef = React.useRef<HTMLTextAreaElement>(null)

  // Drops {{variable}} at the caret so the names never have to be typed.
  function insertVariable(name: string) {
    const el = bodyRef.current
    const token = `{{${name}}}`
    if (!el) return fields.set("body", v.body + token)
    const start = el.selectionStart ?? v.body.length
    const end = el.selectionEnd ?? start
    const next = v.body.slice(0, start) + token + v.body.slice(end)
    fields.set("body", next)
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(start + token.length, start + token.length)
    })
  }

  function sendTest() {
    setTesting(true)
    router.post(`${url}/test`, { to: testTo }, { preserveScroll: true, onFinish: () => setTesting(false) })
  }

  const inUse = template.customised && template.enabled

  return (
    <AdminShell>
      <Head title={`${template.label} — Email`}>
        <meta name="description" content={`Edit the wording of the "${template.label}" email.`} />
        <meta property="og:title" content={`${template.label} — Email`} />
        <meta property="og:description" content={`Edit the wording of the "${template.label}" email.`} />
      </Head>

      <PageHeader
        title="Email"
        description="How the website sends email and where enquiries land. Each box saves on its own tick."
      />

      {flash?.notice && <p className="mt-4 text-sm text-accent">{flash.notice}</p>}
      {flash?.alert && <p className="mt-4 text-sm text-danger">{flash.alert}</p>}

      <EmailLayout nav={email_nav}>
        <div className="rounded-lg border border-hairline bg-white p-5">
          <div className="flex flex-wrap items-start gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2>{template.label}</h2>
                <Badge tone={inUse ? "accent" : "neutral"}>{inUse ? "Custom wording" : "Default wording"}</Badge>
              </div>
              <p className="mt-1 text-sm text-ink-muted">{template.description}</p>
            </div>
            {template.customised && (
              <div className="flex items-center gap-3">
                <span className="text-sm text-ink-body">Use custom wording</span>
                <Switch
                  checked={template.enabled}
                  onChange={(enabled) =>
                    router.patch(url, { email_template: { enabled } }, { preserveScroll: true })
                  }
                  aria-label="Use custom wording"
                />
              </div>
            )}
          </div>

          <div className="mt-6 grid gap-5">
            <InlineField label="Subject" htmlFor="subject" error={errors.subject} state={subjectState}>
              <Input
                id="subject"
                value={v.subject}
                onChange={(e) => fields.set("subject", e.target.value)}
                onKeyDown={subjectState.keys}
                aria-invalid={!!errors.subject}
              />
            </InlineField>
            <InlineField
              label="Body"
              htmlFor="body"
              error={errors.body}
              state={bodyState}
              hint="Plain text. A blank line starts a new paragraph. A line whose variables are all empty is left out of the email."
            >
              <Textarea
                id="body"
                ref={bodyRef}
                rows={18}
                value={v.body}
                onChange={(e) => fields.set("body", e.target.value)}
                onKeyDown={bodyState.multilineKeys}
                aria-invalid={!!errors.body}
                className="font-mono text-[13px] leading-relaxed"
              />
            </InlineField>
          </div>

          <div className="mt-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Variables</p>
            <p className="mt-1 text-sm text-ink-muted">Click one to insert it where the cursor is.</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {template.variables.map((variable) => (
                <button
                  key={variable.name}
                  type="button"
                  onClick={() => insertVariable(variable.name)}
                  title={variable.description}
                  className="rounded-md border border-hairline bg-page px-2 py-1 font-mono text-xs text-ink-display transition-colors hover:border-accent hover:text-accent"
                >
                  {`{{${variable.name}}}`}
                </button>
              ))}
            </div>
            <dl className="mt-3 grid gap-x-6 gap-y-1 text-xs text-ink-muted sm:grid-cols-2">
              {template.variables.map((variable) => (
                <div key={variable.name} className="flex gap-2">
                  <dt className="shrink-0 font-mono text-ink-body">{variable.name}</dt>
                  <dd>{variable.description}</dd>
                </div>
              ))}
            </dl>
          </div>

          {template.customised && (
            <div className="mt-6 border-t border-hairline pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  if (confirm("Discard your wording and go back to the default?")) {
                    router.delete(url, { preserveScroll: true })
                  }
                }}
              >
                Reset to default wording
              </Button>
            </div>
          )}
        </div>

        <div className="mt-6 rounded-lg border border-hairline bg-white p-5">
          <h2>Preview</h2>
          <p className="mt-1 text-sm text-ink-muted">
            The saved wording with sample details filled in. Save a change to see it here.
          </p>
          <div className="mt-4 rounded-md border border-hairline bg-page p-4">
            <p className="text-sm">
              <span className="text-ink-muted">Subject:</span>{" "}
              <span className="font-medium text-ink-display">{template.preview.subject}</span>
            </p>
            <pre className="mt-3 whitespace-pre-wrap font-sans text-sm leading-relaxed text-ink-body">
              {template.preview.text}
            </pre>
          </div>
          <div className="mt-4 flex flex-wrap items-end gap-3">
            <div className="min-w-64 flex-1">
              <label htmlFor="test_to">Send me a test</label>
              <Input
                id="test_to"
                type="email"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="your address (defaults to your login)"
                className="mt-1"
              />
            </div>
            <Button type="button" onClick={sendTest} disabled={testing}>
              {testing ? "Sending…" : "Send test"}
            </Button>
          </div>
        </div>
      </EmailLayout>
    </AdminShell>
  )
}
